import styled from '@emotion/styled';
import { GetServerSideProps, InferGetServerSidePropsType, NextPage } from 'next';

import { Content } from '@/components/NewsArticle/Content';
import { Header } from '@/components/NewsArticle/Header';
import { NewsPageSeo } from '@/components/ui/features/news/NewsPageSeo/NewsPageSeo';
import { Layout } from '@/components/ui/shared/layouts/Layout/Layout';
import { ArticleSocialShare } from '@/components/ui/shared/social/ArticleSocialShare/ArticleSocialShare';
import { accessFrom } from '@/constants/custom-request-headers';
import { AccessFrom } from '@/enums/AccessFrom';
import { getNewsArticleById } from '@/queries/news-articles';
import { IThemeProps } from '@/styles/themes/types';
import { generateStore } from '@/utils/generateStore';
import { splitIdAndSlug } from '@/utils/helperFunctions';
import { Hydrate } from '@/utils/swr/Hydrate';
import { StringParam, urlQueryStringValidate } from '@/utils/url-query-string';

const ArticleShareWrapper = styled.div<IThemeProps>`
  /* content-box so the side padding sits outside the cap - the max-width is then the text's own
     width, not the text plus its gutters (the app sets border-box globally) */
  box-sizing: content-box;
  max-width: 335px;
  margin-left: auto;
  margin-right: auto;
  padding: 0 20px;
  font-size: 16px;
  line-height: 24px;

  @media (min-width: ${({ theme }) => theme.legacy.breakpoints.Tablet.min}px) {
    max-width: 512px;
    font-size: 20px;
    line-height: 32px;
  }

  @media (min-width: ${({ theme }) => theme.legacy.breakpoints.Desktop.min}px) {
    max-width: 800px;
  }
`;

const NewsArticlePage: NextPage<InferGetServerSidePropsType<typeof getServerSideProps>> = ({
  fallback,
  accessFromLocation,
}) => {
  return (
    <Hydrate fallback={fallback}>
      <NewsPageSeo />
      <Layout accessFrom={accessFromLocation} navigationVariant="inverted">
        <Header />
        <Content />
        <ArticleShareWrapper>
          <ArticleSocialShare />
        </ArticleShareWrapper>
      </Layout>
    </Hydrate>
  );
};

export default NewsArticlePage;

export const getServerSideProps: GetServerSideProps = async (context) => {
  const store = generateStore({ context });

  const { id: articleSlug } = urlQueryStringValidate<{ id: string }>(context.query, {
    id: StringParam,
  });

  const { id: articleId } = splitIdAndSlug(articleSlug);

  const article = await store.fetchResource(getNewsArticleById(articleId), {
    dontThrowOnError: (res) => res.status === 404,
  });

  if (!article) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      fallback: store.fallback,
      accessFromLocation: context.req.headers[accessFrom] || AccessFrom.Core,
    },
  };
};
